// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Findings List - renders a list of contract findings with count badge
 * and optional print button.
 */
import React from 'react'
import { cn, Badge, Button } from '@/components/ui'
import { Printer, AlertTriangle } from 'lucide-react'
import { FindingCard } from './FindingCard'
import { formatCount } from '@/applications/contract-review/utils'
import type { ContractFinding } from '@/applications/contract-review/types'

interface Props {
  title?: string
  findings: ContractFinding[]
  itemLabel?: string
  onPrint?: () => void
  className?: string
  documentId?: string
  orgId?: string
  workspaceId?: string
  agentId?: string
  datasetKey?: string
  version?: number
  /** Run these findings came from — identifies the redline that annotates them. */
  run?: number
  documentName?: string
}

export const FindingsList: React.FC<Props> = ({
  title = 'Findings',
  findings,
  itemLabel = 'Finding',
  onPrint,
  className,
  documentId,
  orgId,
  workspaceId,
  agentId,
  datasetKey,
  version,
  run,
  documentName,
}) => {
  const isEmpty = findings.length === 0

  const withPageNumber = findings.filter(f => f.pageNumber != null).length
  const hasNoPageNumbers = findings.length > 0 && withPageNumber === 0
  const hasPartialPageNumbers = withPageNumber > 0 && withPageNumber < findings.length

  if (isEmpty) return null

  return (
    <div data-testid="cr-findings-list" className={cn('space-y-4', className)}>
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="border px-3 py-1 text-xs font-semibold uppercase tracking-wide"
          >
            {formatCount(findings.length, itemLabel)}
          </Badge>
          {onPrint && (
            <Button variant="outline" size="sm" onClick={onPrint} className="gap-1">
              <Printer className="h-4 w-4" />
              Print
            </Button>
          )}
        </div>
      </div>

      {/* Warning when page numbers unavailable */}
      {hasNoPageNumbers && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Findings sorted by risk score. Page numbers unavailable for this document.</span>
        </div>
      )}
      {hasPartialPageNumbers && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Some findings missing page numbers — order may not match document.</span>
        </div>
      )}

      <div className="space-y-4">
        {findings.map((finding, index) => (
          <FindingCard
            key={`${finding.kbItemId}-${index}`}
            finding={finding}
            documentId={documentId}
            orgId={orgId}
            workspaceId={workspaceId}
            agentId={agentId}
            datasetKey={datasetKey}
            version={version}
            run={run}
            findingIndex={finding.originalIndex ?? index}
            documentName={documentName}
          />
        ))}
      </div>
    </div>
  )
}
